import { useState, useEffect, useCallback } from "react";
import { privateApi } from "../../../lib/privateApi";
import { devWarn } from "../../../lib/devLog";

/**
 * Verifies the NDA cookie via /api/private/me (with retry) and exposes
 * a sign-out handler that clears the cookie and bounces to /private/access.
 */
export function usePrivateProjectAccess(navigate) {
    const [authState, setAuthState] = useState("checking");
    const [email, setEmail] = useState("");

    useEffect(() => {
        let cancelled = false;
        privateApi
            .meWithRetry()
            .then((me) => {
                if (cancelled) return;
                setEmail(me.email);
                setAuthState("ok");
            })
            .catch(() => {
                if (cancelled) return;
                navigate("/private/access", { replace: true });
            });
        return () => {
            cancelled = true;
        };
    }, [navigate]);

    const onSignOut = useCallback(async () => {
        try {
            await privateApi.logout();
        } catch (err) {
            // Network failure on logout is non-blocking — the user's intent
            // is already fulfilled locally; the cookie will expire on its
            // own within 4h regardless.
            devWarn("private.logout failed; proceeding with client-side sign-out:", err);
        }
        navigate("/private/access", { replace: true });
    }, [navigate]);

    return { authState, email, onSignOut };
}

/**
 * Loads the requested project + the next project in the carousel order.
 * Handles 401 → /private/access and 404 → /private gracefully.
 */
export function usePrivateProject(slug, authState, navigate) {
    const [project, setProject] = useState(null);
    const [nextProject, setNextProject] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        if (authState !== "ok" || !slug) return;
        let cancelled = false;
        setProject(null);
        setError("");

        (async () => {
            try {
                const [p, list] = await Promise.all([
                    privateApi.project(slug),
                    privateApi.projects(),
                ]);
                if (cancelled) return;
                setProject(p);
                const items = list.items;
                const idx = items.findIndex((x) => x.slug === slug);
                setNextProject(items[(idx + 1) % items.length]);
            } catch (err) {
                if (cancelled) return;
                if (err.status === 401) {
                    navigate("/private/access", { replace: true });
                    return;
                }
                if (err.status === 404) {
                    navigate("/private", { replace: true });
                    return;
                }
                setError(err.message || "Failed to load project.");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [authState, slug, navigate]);

    return { project, nextProject, error };
}
